import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { startOfWeek, endOfWeek, startOfDay, endOfDay } from "date-fns";
import { getTimeIntervalMinutes, CALENDAR_START_HOUR, CALENDAR_END_HOUR, calculateDynamicCalendarHours } from "@/lib/calendarUtils";
import { normalizeBusinessHours } from "@/lib/businessHours";
import {
  getPlainDateKey,
  getTenantTimeZone,
  getUtcRangeForLocalDateRange,
} from "@sylva/shared-types";

export interface CalendarAppointment {
  id: string;
  start_time: string;
  end_time: string;
  status: string;
  customer_id: string;
  customer_name: string;
  customer_email?: string;
  appointment_type_id: string;
  appointment_type_name: string;
  appointment_type_color?: string;
  team_member_id: string | null;
  team_member_name: string;
  team_member_color?: string;
  notes?: string;
  buffer_before?: number;
  buffer_after?: number;
  visit_id: string; // All appointments now have a visit_id (enforced at DB level)
  sequence_index?: number | null;
  resource_id?: string | null;
}

export interface CalendarVisit {
  id: string | null; // null for standalone appointments
  customer_id: string;
  customer_name: string;
  customer_email?: string;
  start_time: string; // Earliest start time of appointments
  end_time: string; // Latest end time of appointments
  status: string;
  notes?: string;
  appointments: CalendarAppointment[];
  // For display purposes
  team_member_id: string | null; // Primary team member (from first appointment)
  team_member_name: string;
  team_member_color?: string;
  buffer_before?: number; // From first appointment
  buffer_after?: number; // From last appointment
}

export interface CalendarFilters {
  teamMemberIds: string[];
  serviceIds: string[];
  searchQuery: string;
  viewType: 'day' | 'week';
}

// Helper function to group appointments into visits
export const groupAppointmentsIntoVisits = (appointments: CalendarAppointment[]): CalendarVisit[] => {
  // Group by visit_id (null means standalone appointment)
  const visitMap = new Map<string | null, CalendarAppointment[]>();

  appointments.forEach(appointment => {
    const visitKey = appointment.visit_id || `standalone-${appointment.id}`;
    if (!visitMap.has(visitKey)) {
      visitMap.set(visitKey, []);
    }
    const visitAppointments = visitMap.get(visitKey);
    if (visitAppointments) {
      visitAppointments.push(appointment);
    }
  });

  // Convert groups to CalendarVisit objects
  const visits: CalendarVisit[] = [];

  visitMap.forEach((appointmentsInVisit, visitKey) => {
    // Sort appointments by sequence_index (or start_time if no sequence)
    const sortedAppointments = [...appointmentsInVisit].sort((a, b) => {
      if (a.sequence_index !== null && b.sequence_index !== null && a.sequence_index !== undefined && b.sequence_index !== undefined) {
        return a.sequence_index - b.sequence_index;
      }
      return new Date(a.start_time).getTime() - new Date(b.start_time).getTime();
    });

    const firstAppointment = sortedAppointments[0];
    const lastAppointment = sortedAppointments[sortedAppointments.length - 1];

    // Find earliest start and latest end
    const startTimes = sortedAppointments.map(a => new Date(a.start_time).getTime());
    const endTimes = sortedAppointments.map(a => new Date(a.end_time).getTime());
    const earliestStart = new Date(Math.min(...startTimes)).toISOString();
    const latestEnd = new Date(Math.max(...endTimes)).toISOString();

    // Determine visit status (if any appointment is no_show, visit is no_show, else use first)
    const hasNoShow = sortedAppointments.some(a => a.status === 'no_show');
    const status = hasNoShow ? 'no_show' : firstAppointment.status;

    visits.push({
      id: visitKey && visitKey.startsWith('standalone-') ? null : visitKey,
      customer_id: firstAppointment.customer_id,
      customer_name: firstAppointment.customer_name,
      customer_email: firstAppointment.customer_email,
      start_time: earliestStart,
      end_time: latestEnd,
      status,
      notes: firstAppointment.notes,
      appointments: sortedAppointments,
      team_member_id: firstAppointment.team_member_id,
      team_member_name: firstAppointment.team_member_name,
      team_member_color: firstAppointment.team_member_color,
      buffer_before: firstAppointment.buffer_before,
      buffer_after: lastAppointment.buffer_after,
    });
  });

  // Sort visits by start time
  return visits.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
};

// Helper function to detect if a visit has multiple team members assigned
export const hasMultipleTeamMembers = (visit: CalendarVisit): boolean => {
  const teamMemberIds = new Set(
    visit.appointments
      .map(apt => apt.team_member_id)
      .filter(id => id) // Filter out null/undefined
  );
  return teamMemberIds.size > 1;
};

export const useCalendarData = (date: Date, filters: CalendarFilters) => {
  const { currentTenant } = useTenant();
  const queryClient = useQueryClient();

  const startDate = filters.viewType === 'day'
    ? startOfDay(date)
    : startOfWeek(date, { weekStartsOn: 1 });

  const endDate = filters.viewType === 'day'
    ? endOfDay(date)
    : endOfWeek(date, { weekStartsOn: 1 });
  const tenantTimeZone = getTenantTimeZone(currentTenant?.timezone);

  const query = useQuery({
    queryKey: ['calendar-appointments', currentTenant?.id, currentTenant?.timezone, startDate, endDate, filters.teamMemberIds, filters.serviceIds, filters.viewType],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      const { start, endExclusive } = getUtcRangeForLocalDateRange(
        getPlainDateKey(startDate),
        getPlainDateKey(endDate),
        tenantTimeZone,
      );

      // Fetch ALL appointments without filtering (we'll filter at visit level later)
      const { data, error } = await supabase
        .from('appointments')
        .select(`
          id,
          start_time,
          end_time,
          status,
          notes,
          appointment_type_id,
          customer_id,
          team_member_id,
          visit_id,
          sequence_index,
          customers (
            first_name,
            last_name,
            email
          ),
          appointment_types (
            title,
            color,
            buffer_before,
            buffer_after
          ),
          team_members (
            id,
            name,
            color
          )
        `)
        .eq('tenant_id', currentTenant.id)
        .gte('start_time', start.toISOString())
        .lt('start_time', endExclusive.toISOString())
        .neq('status', 'cancelled')
        .order('start_time', { ascending: true });

      if (error) throw error;

      const appointments: CalendarAppointment[] = (data || []).map(appointment => ({
        id: appointment.id,
        start_time: appointment.start_time,
        end_time: appointment.end_time,
        status: appointment.status || 'confirmed',
        customer_id: appointment.customer_id || '',
        customer_name: appointment.customers
          ? `${appointment.customers.first_name}${appointment.customers.last_name ? ` ${appointment.customers.last_name}` : ''}`.trim()
          : 'Unknown Customer',
        customer_email: appointment.customers?.email ?? undefined,
        appointment_type_id: appointment.appointment_type_id,
        appointment_type_name: appointment.appointment_types?.title || 'Unknown Type',
        appointment_type_color: appointment.appointment_types?.color || '#22c55e',
        team_member_id: appointment.team_member_id || null,
        team_member_name: appointment.team_members?.name || 'Unknown Member',
        team_member_color: appointment.team_members?.color || '#3b82f6',
        notes: appointment.notes ?? undefined,
        buffer_before: appointment.appointment_types?.buffer_before || 0,
        buffer_after: appointment.appointment_types?.buffer_after || 0,
        visit_id: appointment.visit_id,
        sequence_index: appointment.sequence_index,
      }));

      // Group into visits first
      const visits = groupAppointmentsIntoVisits(appointments);

      // Apply filters at the VISIT level
      const filteredVisits = visits.filter(visit => {
        // Check team member filter - visit matches if ANY appointment has a matching team member
        // Empty array means "no filter" - show all visits
        if (filters.teamMemberIds.length > 0) {
          const hasMatchingTeamMember = visit.appointments.some(apt =>
            apt.team_member_id && filters.teamMemberIds.includes(apt.team_member_id)
          );
          if (!hasMatchingTeamMember) return false;
        }

        // Check service filter - visit matches if ANY appointment has a matching service
        // Empty array means "no filter" - show all visits (including those with deleted services)
        // This is crucial: when no services are filtered (all checkboxes checked or default state),
        // we pass an empty array and show ALL visits, even if they contain appointments with deleted services.
        if (filters.serviceIds.length > 0) {
          const hasMatchingService = visit.appointments.some(apt =>
            filters.serviceIds.includes(apt.appointment_type_id)
          );
          if (!hasMatchingService) return false;
        }

        return true;
      });

      // Flatten back to appointments for backward compatibility
      const filteredAppointments = filteredVisits.flatMap(visit => visit.appointments);

      return filteredAppointments;
    },
    enabled: !!currentTenant?.id,
  });

  // Subscribe to real-time updates using broadcast
  useEffect(() => {
    if (!currentTenant?.id) return undefined;

    // Create separate channels for appointments and visits
    const appointmentsChannel = supabase
      .channel(`tenant:${currentTenant.id}:appointments`, {
        config: { private: true }
      })
      .on(
        'broadcast',
        { event: '*' },
        (payload) => {
          console.log('[Appointments] Broadcast received:', payload);
          queryClient.invalidateQueries({ queryKey: ['calendar-appointments', currentTenant.id] });
        }
      );

    const visitsChannel = supabase
      .channel(`tenant:${currentTenant.id}:visits`, {
        config: { private: true }
      })
      .on(
        'broadcast',
        { event: '*' },
        (payload) => {
          console.log('[Visits] Broadcast received:', payload);
          queryClient.invalidateQueries({ queryKey: ['calendar-appointments', currentTenant.id] });
        }
      );

    // Set auth and subscribe
    const setupChannels = async () => {
      await supabase.realtime.setAuth();
      await appointmentsChannel.subscribe();
      await visitsChannel.subscribe();
    };

    setupChannels();

    return () => {
      supabase.removeChannel(appointmentsChannel);
      supabase.removeChannel(visitsChannel);
    };
  }, [currentTenant?.id, queryClient]);

  // Filter by search query using useMemo to avoid re-fetching
  const filteredData = useMemo(() => {
    if (!query.data) return [];

    if (!filters.searchQuery) return query.data;

    const searchLower = filters.searchQuery.toLowerCase();
    // For search, also filter at visit level to keep visits intact
    const visits = groupAppointmentsIntoVisits(query.data);
    const matchingVisits = visits.filter(visit =>
      visit.customer_name.toLowerCase().includes(searchLower) ||
      visit.appointments.some(apt =>
        apt.appointment_type_name.toLowerCase().includes(searchLower) ||
        apt.team_member_name.toLowerCase().includes(searchLower)
      )
    );
    return matchingVisits.flatMap(visit => visit.appointments);
  }, [query.data, filters.searchQuery]);

  return {
    ...query,
    data: filteredData,
  };
};

// New hook to get visit-based calendar data
export const useCalendarVisits = (date: Date, filters: CalendarFilters) => {
  const appointmentsQuery = useCalendarData(date, filters);

  const visits = useMemo(() => {
    if (!appointmentsQuery.data) return [];
    return groupAppointmentsIntoVisits(appointmentsQuery.data);
  }, [appointmentsQuery.data]);

  return {
    ...appointmentsQuery,
    data: visits,
  };
};

export const useCalendarTimeSlots = () => {
  const { currentTenant } = useTenant();

  const globalAvail = currentTenant?.settings?.globalAvailability;
  const intervalMinutes = getTimeIntervalMinutes(
    globalAvail?.startTimeInterval,
    globalAvail?.customIntervalMinutes
  );

  // Calculate dynamic calendar hours based on business hours
  const dynamicCalendarHours = useMemo(() => {
    const interval = getTimeIntervalMinutes(
      currentTenant?.settings?.globalAvailability?.startTimeInterval,
      currentTenant?.settings?.globalAvailability?.customIntervalMinutes
    );
    if (!currentTenant?.settings?.businessHours) {
      return {
        startHour: CALENDAR_START_HOUR,
        startMinute: 0,
        endHour: CALENDAR_END_HOUR,
        endMinute: 0
      };
    }

    const businessHours = normalizeBusinessHours(currentTenant.settings.businessHours);
    return calculateDynamicCalendarHours(businessHours, interval);
  }, [currentTenant]);

  const generateTimeSlots = () => {
    const { startHour, startMinute = 0, endHour, endMinute = 0 } = dynamicCalendarHours;
    const endTotalMinutes = endHour * 60 + endMinute;

    const slots = [];
    for (let hour = startHour; hour <= endHour; hour++) {
      const minStart = (hour === startHour) ? startMinute : 0;
      for (let minute = minStart; minute < 60; minute += intervalMinutes) {
        const currentTotalMinutes = hour * 60 + minute;
        if (currentTotalMinutes > endTotalMinutes) break;
        const timeSlot = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(timeSlot);
      }
    }
    return slots;
  };

  return generateTimeSlots();
};

export const useCalendarBusinessHours = (targetDate?: Date) => {
  const { currentTenant } = useTenant();

  const globalAvail = currentTenant?.settings?.globalAvailability;
  const intervalMinutes = getTimeIntervalMinutes(
    globalAvail?.startTimeInterval,
    globalAvail?.customIntervalMinutes
  );

  // Calculate dynamic calendar hours based on business hours
  const dynamicCalendarHours = useMemo(() => {
    const interval = getTimeIntervalMinutes(
      currentTenant?.settings?.globalAvailability?.startTimeInterval,
      currentTenant?.settings?.globalAvailability?.customIntervalMinutes
    );
    if (!currentTenant?.settings?.businessHours) {
      return {
        startHour: CALENDAR_START_HOUR,
        startMinute: 0,
        endHour: CALENDAR_END_HOUR,
        endMinute: 0
      };
    }

    const businessHours = normalizeBusinessHours(currentTenant.settings.businessHours);
    return calculateDynamicCalendarHours(businessHours, interval);
  }, [currentTenant]);

  const generateAllTimeSlots = () => {
    const { startHour, startMinute = 0, endHour, endMinute = 0 } = dynamicCalendarHours;
    const endTotalMinutes = endHour * 60 + endMinute;

    const slots = [];
    for (let hour = startHour; hour <= endHour; hour++) {
      const minStart = (hour === startHour) ? startMinute : 0;
      for (let minute = minStart; minute < 60; minute += intervalMinutes) {
        const currentTotalMinutes = hour * 60 + minute;
        if (currentTotalMinutes > endTotalMinutes) break;
        const timeSlot = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(timeSlot);
      }
    }
    return slots;
  };

  // Get business hours from tenant settings
  const getBusinessHoursForDay = (date: Date = new Date()) => {
    if (!currentTenant?.settings?.businessHours) {
      // Fallback to default if no business hours configured
      return {
        available: true,
        timeRanges: [{ start: "08:00", end: "18:00" }]
      };
    }

    const businessHours = currentTenant.settings.businessHours;
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    const dayName = dayNames[date.getDay()];

    // Type-safe access to day-specific business hours
    const dayHours = (businessHours as Record<string, unknown>)[dayName];
    if (dayHours && typeof dayHours === 'object' && 'available' in dayHours && 'timeRanges' in dayHours) {
      return dayHours as { available: boolean; timeRanges: { start: string; end: string }[] };
    }

    return {
      available: false,
      timeRanges: []
    };
  };

  const isBusinessHour = (timeSlot: string, date: Date = targetDate || new Date()) => {
    const dayHours = getBusinessHoursForDay(date);

    if (!dayHours.available) return false;

    return dayHours.timeRanges.some((range: { start: string; end: string }) => {
      return timeSlot >= range.start && timeSlot < range.end;
    });
  };

  const allTimeSlots = generateAllTimeSlots();
  const businessTimeSlots = allTimeSlots.filter(slot => isBusinessHour(slot));
  const offBusinessTimeSlots = allTimeSlots.filter(slot => !isBusinessHour(slot));

  return {
    getBusinessHoursForDay,
    allTimeSlots,
    businessTimeSlots,
    offBusinessTimeSlots,
    isBusinessHour,
    dynamicCalendarHours
  };
};
